import { useCallback, useEffect, useMemo, useState } from 'react';
import { isNil, upperFirst } from 'lodash';
import { useLocation } from 'react-router-dom';

const defaultSortable = { allowUnsort: true, mode: 'multiple' };
const defaultInitialPage = { skip: 0, take: 25 };
const defaultColumns = [];

const useSortPageState = ({
  initialSort,
  sortable = defaultSortable,
  isMultiSorting = null,
  initialPage = defaultInitialPage,
  columns = defaultColumns,
  enableSortCache = true,
  gridName = '',
}) => {
  const location = useLocation();
  // Sorting
  const innerSortable = useMemo(() => {
    if (sortable === false) {
      return false;
    }
    if (isNil(isMultiSorting)) {
      return { ...defaultSortable, ...sortable };
    }
    if (isMultiSorting) {
      return defaultSortable;
    }
    return { allowUnsort: true, mode: 'single' };
  }, [isMultiSorting, sortable]);

  // Map to columns and filter from unsortable
  const buildSortingState = useCallback((sort = []) => sort.map((s) => ({
    ...s,
    sortBy: upperFirst(columns.find((c) => c.field === s.field)?.sortBy || s.field),
    avoidSort: columns.find((c) => c.field === s.field)?.avoidSort ?? false,
  })).filter(({ avoidSort, sortable }) => (!avoidSort && sortable !== false)), [columns]);

  // Get sorting params for request
  const buildSortingParams = useCallback((sort = []) => {
    if (sort.length === 0 || sortable === false) {
      return {};
    }

    const { mode } = innerSortable;
    const allowMultiple = mode === 'multiple';

    if (!allowMultiple) {
      const [{ dir, sortBy }] = sort;
      return {
        sort: sortBy,
        order: dir === 'asc' ? 1 : -1,
      };
    }

    const sortFields = sort.map(({ sortBy }) => sortBy);
    const sortDir = sort.map(({ dir }) => (dir === 'asc' ? 1 : -1));

    return { sortFields, sortDir };
  }, [innerSortable.mode, initialSort]);

  // Telerik components handler
  const onSortChange = ({ sort: newSort }) => {
    const value = newSort && newSort.length > 0 ? newSort : undefined;
    const updated = buildSortingState(value);
    setSort(updated);
  };

  const getPageSizeLSKey = () => `user_page_size${location?.pathname.replace(/\//g, '_')}`;
  const getSortLSKey = () => `user_sort_${gridName.toLowerCase().replace(/ /g, '_')}_${location?.pathname.replace(/\//g, '_')}`;
  const clearSortCache = () => window?.localStorage?.removeItem?.(getSortLSKey());
  // Pagination
  const getInitialPage = () => {
    if (enableSortCache) {
      const userPageSize = Number(window?.localStorage?.getItem(getPageSizeLSKey()));
      if (!Number.isNaN(userPageSize) && userPageSize > 0) {
        return {
          ...initialPage,
          take: userPageSize,
        };
      }
    }

    return initialPage;
  };

  const getInitialSort = () => {
    if (enableSortCache) {
      const sortDt = JSON.parse(window?.localStorage?.getItem(getSortLSKey()));
      if (sortDt != null) {
        return sortDt;
      }
    }

    return initialSort;
  };

  const [page, setPage] = useState(() => getInitialPage());

  const [sort, setSort] = useState(() => buildSortingState(getInitialSort()));
  const sortParams = useMemo(() => buildSortingParams(sort), [sort]);
  // Get pagination params for request
  const buildPageParams = useCallback((page = initialPage) => ({
    pageSize: page.take,
    page: page.skip / page.take + 1,
  }), [initialPage]);

  const onPageChange = ({ page }) => {
    if (enableSortCache && typeof page?.take === 'number' && page?.take > 0) {
      window?.localStorage?.setItem?.(getPageSizeLSKey(), page?.take);
    }
    setPage(page);
  };

  const pageParams = useMemo(() => buildPageParams(page), [page]);

  const resetSort = () => {
    setSort(buildSortingState());
  };

  useEffect(() => {
    window?.localStorage?.setItem?.(getSortLSKey(), JSON.stringify(sort));
  }, [sort]);

  return {
    // Telerik components props
    innerSortable,
    onSortChange,
    onPageChange,
    resetSort,
    clearSortCache,
    page,
    sort: innerSortable ? sort : undefined,
    // Request params
    sortParams,
    pageParams,
  };
};

export default useSortPageState;
